import { defineStore } from '@radical/stores'
import { BASIC_HOME_PATH } from '@radical/constants'
import { router } from '@radical/router'
import {
  doLogin,
  LoginParams,
  getUserInfo,
  logout,
  changePassword,
  IUpdatePassword,
  UserInfoModel,
} from '@/apis/sys/auth'
import { useGo, useMessage } from '@radical/hooks'
import { useI18n } from '@radical/locale'

interface UserState {
  userInfo: Nullable<UserInfoModel>
  token: string
  roles: string[]
}

export const useUserStore = defineStore({
  id: 'app-user-store',
  persist: {
    paths: ['userInfo', 'token', 'newToken'],
  },
  state: (): UserState => ({
    userInfo: null,
    token: '',
    roles: [],
  }),
  getters: {
    getUserInfo(): UserInfoModel | null {
      return this.userInfo
    },
    getToken(): string {
      return this.token
    },
    getRoles(): string[] {
      return this.roles.length > 0 ? this.roles : []
    },
  },
  actions: {
    setUserInfo(info: UserInfoModel | null) {
      this.userInfo = info
    },
    setToken(token: string) {
      this.token = token
    },
    setRoles(roles: string[]) {
      this.roles = roles
    },
    async login(params: LoginParams): Promise<UserInfoModel | null> {
      const go = useGo(router)
      try {
        const { token, userInfo } = await doLogin(params)
        this.setToken(token)
        this.setUserInfo(userInfo)

        const redirect = router.currentRoute.value.query?.redirect as string
        if (redirect) {
          go(decodeURIComponent(redirect), true)
        } else {
          go(BASIC_HOME_PATH, true)
        }
        return this.userInfo
      } catch (error) {
        return Promise.reject(error)
      }
    },
    // 使用token获取用户信息
    async getLoginInfo(): Promise<Boolean> {
      try {
        // if (!this.token) {
        //   return Promise.reject(new Error('No token found'));
        // }
        const userInfo = await getUserInfo()
        this.setUserInfo(userInfo)
        return Promise.resolve(true)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async updatePassWord(params: IUpdatePassword) {
      try {
        await changePassword(params)
        return Promise.resolve(true)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    /**
     * @description: 登出确认
     */
    confirmLoginOut() {
      const { createConfirm } = useMessage()
      const { t } = useI18n()
      createConfirm({
        iconType: 'warning',
        title: () => t('common.退出确认'),
        content: () => t('common.确认退出系统吗'),
        okText: t('common.退出'),
        cancelText: t('common.取消'),
        onOk: () => this.loginOut(),
      })
    },
    /**
     * @description: 退出登录
     */
    loginOut() {
      this.setUserInfo(null)
      this.setToken('')
      // TODO: logout
      location.reload()
    },
  },
})

// 需要在 setup 外使用时
export function useUserStoreWithout() {
  return useUserStore()
}
